import React, { useEffect, useRef, useState } from 'react';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import { twMerge } from 'tailwind-merge';

const AUTO_ROTATE_MS = 3000;

// Subtitles longer than this can't fit the fixed card at the normal type size,
// so they step down instead of spilling out. Only the QA "Languages, 2
// Frameworks — Java/Selenium and TypeScript/Playwright…" copy crosses it today.
const LONG_SUBTITLE_CHARS = 60;

const HighlightCard = ({ item, cardWidth, cardGap, cardPadding }) => {
  const image = getImage(item.image.childImageSharp);
  const isLongSubtitle = (item.subtitle?.length || 0) > LONG_SUBTITLE_CHARS;

  return (
    <div
      className={`flex h-[260px] ${cardWidth} flex-shrink-0 flex-col items-center ${cardGap} rounded-2xl bg-white ${cardPadding} py-6 text-center font-rubik drop-shadow-lg`}
    >
      <div className="flex h-16 w-16 flex-shrink-0 items-center justify-center">
        <GatsbyImage
          image={image}
          alt={item.subtitle}
          imgStyle={{ objectFit: 'contain' }}
        />
      </div>

      {/* Stat Number */}
      <div className="flex-shrink-0 text-5xl font-bold leading-none tracking-tight text-gray-900">
        {item.title}
      </div>

      {/* Subtitle */}
      {/* max-w caps the text column: with a `w-fit` card (4 highlights) it is
          what keeps the card from stretching to the full one-line text width. */}
      <p
        className={`w-full max-w-[170px] break-words font-semibold text-gray-600 ${
          isLongSubtitle ? 'text-[10px] leading-tight' : 'text-sm leading-snug'
        }`}
      >
        {item.subtitle}
      </p>
    </div>
  );
};

const ProgramHighlights = ({
  heading,
  subtitle,
  programHighlights,
  className,
}) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);
  const touchStartX = useRef(null);
  const trackRef = useRef(null);
  const isDragging = useRef(false);

  const count = programHighlights?.length || 0;

  useEffect(() => {
    if (isPaused || count <= 1) return undefined;
    const id = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % count);
    }, AUTO_ROTATE_MS);
    return () => clearInterval(id);
  }, [isPaused, count]);

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    isDragging.current = true;
    setIsPaused(true);
  };

  const handleTouchMove = (e) => {
    if (!isDragging.current || touchStartX.current === null) return;
    setDragOffset(e.touches[0].clientX - touchStartX.current);
  };

  const handleTouchEnd = () => {
    const width = trackRef.current?.offsetWidth || 1;
    // Advance when the swipe covers more than 20% of the card width.
    const threshold = width * 0.2;
    if (dragOffset <= -threshold && activeIndex < count - 1) {
      setActiveIndex((prev) => prev + 1);
    } else if (dragOffset >= threshold && activeIndex > 0) {
      setActiveIndex((prev) => prev - 1);
    }
    touchStartX.current = null;
    isDragging.current = false;
    setDragOffset(0);
    setIsPaused(false);
  };

  if (!programHighlights) {
    return null;
  }

  const cardWidth = count === 4 ? 'w-fit' : count === 5 ? 'w-48' : 'w-40';
  const cardGap = count === 4 ? 'gap-8' : count === 5 ? 'gap-5' : 'gap-4';
  // Tighter horizontal padding as cards get narrower, so long labels
  // (e.g. "TypeScript/Playwright") have room to wrap instead of overflowing.
  const cardPadding = count === 4 ? 'px-8' : count === 5 ? 'px-6' : 'px-4';

  return (
    <div className={twMerge('container px-6 pt-10', className)}>
      {/* Title and Subtitle Section */}
      <div className="mb-10 text-center">
        <h1 className="font-manrope text-2xl font-extrabold text-gray-900 sm:text-4xl">
          {heading}
        </h1>
        <h3 className="mt-4 font-rubik text-base font-normal text-gray-600">
          {subtitle}
        </h3>
      </div>

      {/* Desktop / tablet: full row */}
      <div className="hidden flex-nowrap items-stretch justify-center gap-5 overflow-x-auto pb-4 sm:flex">
        {programHighlights.map((item, index) => (
          <HighlightCard
            key={index}
            item={item}
            cardWidth={cardWidth}
            cardGap={cardGap}
            cardPadding={cardPadding}
          />
        ))}
      </div>

      {/* Mobile: one card at a time, auto-rotating carousel */}
      <div className="sm:hidden">
        <div
          ref={trackRef}
          className="touch-pan-y overflow-hidden"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <div
            className={`flex ${
              isDragging.current
                ? ''
                : 'transition-transform duration-500 ease-out'
            }`}
            style={{
              transform: `translateX(calc(-${activeIndex * 100}% + ${dragOffset}px))`,
            }}
          >
            {programHighlights.map((item, index) => (
              <div
                key={index}
                className="flex w-full flex-shrink-0 justify-center px-2"
              >
                <HighlightCard
                  item={item}
                  cardWidth="w-64"
                  cardGap="gap-4"
                  cardPadding="px-6"
                />
              </div>
            ))}
          </div>
        </div>

        {/* Dots */}
        {count > 1 && (
          <div className="mt-6 flex items-center justify-center gap-2">
            {programHighlights.map((_, index) => (
              <button
                key={index}
                type="button"
                aria-label={`Go to highlight ${index + 1}`}
                onClick={() => setActiveIndex(index)}
                className={`h-2 rounded-full transition-all duration-300 ${
                  activeIndex === index
                    ? 'w-6 bg-v5-green-300'
                    : 'w-2 bg-gray-300'
                }`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProgramHighlights;
